import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useFacebookToken } from '@/hooks/useFacebookToken';
import {
  TimeFrame,
  useInstagramAccountOverview,
} from '@/hooks/useInstagramAccountOverview';
import { useTeams } from '@/hooks/useTeams';
import { motion } from 'framer-motion';
import { Filter } from 'lucide-react';
import { useState } from 'react';

export const OverviewCard = () => {
  const [timeFrame, setTimeFrame] = useState<TimeFrame>(TimeFrame.Day);
  const { activeTeam } = useTeams();
  const { needsReauthentication, isLoading: checkingFacebookToken } =
    useFacebookToken();

  const accountId = activeTeam.data?.facebook_account?.account_id;

  const { data, isLoading, error } = useInstagramAccountOverview(
    accountId || '',
    timeFrame,
  );

  if (checkingFacebookToken || (isLoading && !data)) {
    return <SkeletonLoader />;
  }

  if (!accountId || needsReauthentication || checkingFacebookToken || error) {
    return null;
  }

  const handleTimeFrameChange = (value: string) => {
    setTimeFrame(value as TimeFrame);
  };

  const getLatestValue = (metricName: string) => {
    // Look for the metric in the insights data array
    const metric = data?.insights.data.find((d) => d.name === metricName);
    if (metric) {
      return metric.values[metric.values.length - 1].value;
    }

    // If not found in insights, check the root level properties
    return data?.[metricName as keyof typeof data] || 0;
  };

  const calculatePercentChange = (values: { value: number }[]) => {
    const current = values.at(-1)?.value || 0;
    const previous = values.at(-2)?.value || 0;

    if (previous === 0 || !isFinite(previous) || !isFinite(current)) {
      return { percentChange: 0, isPositive: false, shouldShow: false };
    }

    const percentChange = ((current - previous) / previous) * 100;

    if (isNaN(percentChange)) {
      return { percentChange: 0, isPositive: false, shouldShow: false };
    }

    return {
      percentChange,
      isPositive: percentChange > 0,
      shouldShow: true,
    };
  };

  const calculateAbsoluteChange = (values: { value: number }[]) => {
    const current = values.at(-1)?.value || 0;
    const previous = values.at(-2)?.value || 0;

    return current - previous;
  };

  const formatTimeFrame = (time: TimeFrame) => {
    switch (time) {
      case TimeFrame.Day:
        return 'day';
      case TimeFrame.Week:
        return 'week';
      case TimeFrame.Month:
        return '28 days';
      default:
        return 'period';
    }
  };

  const formatAbsoluteChange = (change: number) => {
    if (change === 0) return '0';

    return `${change > 0 ? '+' : '-'}${Math.abs(change).toLocaleString()}`;
  };

  const mainMetric = data?.insights.data.find(
    (d) => d.name === 'follower_count',
  );
  const { percentChange, isPositive, shouldShow } = calculatePercentChange(
    mainMetric?.values || [],
  );

  return (
    <Card className="p-5 h-max w-full">
      <div className="flex flex-col gap-2">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <h2 className="text-lg font-semibold">Channel Analytics</h2>
        </motion.div>

        <div className=" flex flex-col">
          <div className="flex justify-between mb-2">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Current followers</p>
              <div className="flex items-baseline gap-2">
                <h3 className="text-3xl font-bold">
                  {data?.followers_count.toLocaleString()}
                </h3>
                {shouldShow && (
                  <p
                    className={`text-sm font-bold ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
                    {isPositive ? '+' : ''}
                    {percentChange.toFixed(1)}%
                  </p>
                )}
              </div>
            </div>
            <Select value={timeFrame} onValueChange={handleTimeFrameChange}>
              <SelectTrigger className="w-14 h-10 px-2">
                <Filter className="h-4 w-4 mr-2" />
              </SelectTrigger>
              <SelectContent align="end">
                <SelectItem value={TimeFrame.Day}>Daily</SelectItem>
                <SelectItem value={TimeFrame.Week}>Weekly</SelectItem>
                <SelectItem value={TimeFrame.Month}>Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-2">Summary</h3>
            <div className="space-y-1">
              {data?.insights.data
                .filter((item) => item.name !== 'follower_count')
                .map((insight) => {
                  const {
                    percentChange: change,
                    isPositive: positive,
                    shouldShow: show,
                  } = calculatePercentChange(insight.values);

                  return (
                    <div
                      key={insight.name}
                      className="flex justify-between items-center">
                      <p className="text-sm text-muted-foreground">
                        {insight.title}
                      </p>
                      <div className="flex items-baseline gap-2">
                        <p className="font-medium">
                          {insight.values[
                            insight.values.length - 1
                          ].value.toLocaleString()}
                        </p>
                        {show && (
                          <p
                            className={`text-xs font-medium ${positive ? 'text-green-500' : 'text-red-500'}`}>
                            {positive ? '+' : ''}
                            {change.toFixed(1)}%
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const SkeletonLoader = () => {
  return (
    <Card className="p-5 h-max w-full">
      <div className="flex flex-col gap-4">
        <Skeleton className="h-7 w-48" />

        <div className="space-y-6">
          <div className="flex justify-between mb-2">
            <div className="space-y-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-9 w-40" />
              <Skeleton className="h-4 w-36" />
            </div>
            <Skeleton className="h-10 w-14" />
          </div>

          <div>
            <Skeleton className="h-5 w-20 mb-2" />
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex justify-between items-center">
                  <Skeleton className="h-4 w-32" />
                  <div className="flex items-baseline gap-2">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-12" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
